import { TUserStats, TUserStatsResponse, useUsersStats } from 'shared/helpers/use-stats-hook';
import { useEffect, useMemo } from 'react';
import { Box, Card, CardContent, CardHeader, styled } from '@mui/material';
import { colorLabel } from 'shared/Providers/MuiTheme';
import { prettySize } from 'shared/utils/format';
import { IconLocalStorage } from 'shared/_ui/icon';

export const StatProgressBarContainer = styled('div', { name: 'StatItemContainer' })(
	({ theme }) => ({
		marginBottom: theme.spacing(5),
		width: '100%',
		height: theme.spacing(2),
		background: '#f1f5f9',
		borderRadius: theme.spacing(1),
		overflow: 'hidden',
		display: 'flex',
	})
);

type StatProgressBarProps = { data: { color: string; percentage: number | undefined }[] };
function StatProgressBar({ data }: StatProgressBarProps) {
	return (
		<StatProgressBarContainer>
			{data.map(({ color, percentage }) => (
				<Box width={`${percentage ?? 0}%`} bgcolor={color} />
			))}
		</StatProgressBarContainer>
	);
}

export const StatItemContainer = styled('div', { name: 'StatItemContainer' })(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(2),
	padding: theme.spacing(2),
	background: '#f8fafc',
	borderRadius: theme.spacing(2),
}));
export const StatItemColor = styled('div', { name: 'StatItemColor' })(({ theme }) => ({
	width: theme.spacing(3),
	height: theme.spacing(3),
	borderRadius: theme.spacing(0.5),
}));
export const StatItemLabel = styled('div', { name: 'StatItemLabel' })(({ theme }) => ({
	fontSize: theme.spacing(3),
	color: colorLabel,
}));
export const StatItemValue = styled('div', { name: 'StatItemValue' })({
	flex: 1,
	fontWeight: 600,
	fontSize: '13px',
});

type StatItemProps = { color: string; label: string; value?: string };
function StatItem(props: StatItemProps) {
	const { color, label, value } = props;
	return (
		<StatItemContainer>
			<StatItemColor sx={{ bgcolor: color }} />
			<StatItemValue>
				<StatItemLabel>{label}</StatItemLabel>
				<StatItemValue>{value}</StatItemValue>
			</StatItemValue>
		</StatItemContainer>
	);
}

const COLOR_VIDEOS = '#10b981';
const COLOR_AUDIO = '#3b82f6';
const COLOR_PROJECTS = '#f59e0b';
const MOCK_TOTAL_QUOTA = Math.round((100 * 1024 * 1024 * 1024) / 1000000) * 1000000;

export default function UserStatsCard() {
	const { userStats, readUserStats } = useUsersStats({ autoRead: false });
	const totalQuota = MOCK_TOTAL_QUOTA; // TODO: User's quota
	const data = useMemo<
		Partial<Record<keyof TUserStats, TUserStatsResponse & { percentage: number }>> & {
			total: string;
			totalRaw: number;
		}
	>(() => {
		const totalRaw = userStats.reduce((acc, { valRaw }) => acc + valRaw, 0);

		const grouped = Object.fromEntries(
			userStats?.map((item) => [
				item.key,
				{ ...item, percentage: totalQuota ? (item.valRaw / totalQuota) * 100 : 0 },
			]) ?? []
		);

		return {
			...grouped,
			total: prettySize(totalRaw),
			totalRaw,
		};
	}, [userStats, totalQuota]);

	useEffect(() => {
		readUserStats();
	}, [readUserStats]);

	return (
		<Card>
			<CardHeader avatar={<IconLocalStorage />} title="Storage Usage" />
			<CardContent>
				<Box textAlign="center" mb={5}>
					<Box fontSize="2rem" fontWeight={700}>
						{data.total}
					</Box>
					<Box color="text.secondary" mt={1}>
						of {prettySize(totalQuota)} used
					</Box>
				</Box>

				<StatProgressBar
					data={[
						{ color: COLOR_VIDEOS, percentage: data.user_all_final_videos_files_size?.percentage },
						{ color: COLOR_AUDIO, percentage: data.user_all_final_audio_files_size?.percentage },
						{ color: COLOR_PROJECTS, percentage: data.user_all_files_size?.percentage },
					]}
				/>

				<Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
					<StatItem
						color={COLOR_VIDEOS}
						label="Videos"
						value={data.user_all_final_videos_files_size?.val}
					/>
					<StatItem
						color={COLOR_AUDIO}
						label="Audio"
						value={data.user_all_final_audio_files_size?.val}
					/>
					<StatItem color={COLOR_PROJECTS} label="Projects" value={data.user_all_files_size?.val} />
					<StatItem
						color="#e5e7eb"
						label="Available"
						value={data.user_all_files_size?.val ? prettySize(totalQuota - data.totalRaw) : ''}
					/>
				</Box>
			</CardContent>
		</Card>
	);
}
